// SPDX-License-Identifier: AGPL-3.0-or-later
import {} from './languages.js';
const DICTIONARY = {
    en: {
        'submit.idle': 'Submit Response',
        'submit.pending': 'Submitting…',
        'submit.success.heading': 'Submission Successful',
        'submit.success.body': 'Thank you! Your responses have been securely recorded and sent to your healthcare provider. You can safely close this window now.',
        'submit.success.summary': 'Submitted Answers Summary',
        'submit.success.no-answers': 'No questions answered.',
        'submit.response-recorded': 'Response recorded',
        'autosave.unsaved': 'Unsaved changes',
        'autosave.not-saved': 'Not yet saved',
        'autosave.just-now': 'Saved just now',
        'autosave.seconds': (a) => `Saved ${String(a['n'])}s ago`,
        'autosave.minutes': (a) => `Saved ${String(a['n'])}m ago`,
        'autosave.hours': (a) => `Saved ${String(a['n'])}h ago`,
        'header.required-count': (a) => `${String(a['done'])} / ${String(a['total'])} required`,
        'header.no-required': 'No required questions',
        'loading.form': 'Loading your form',
        'empty.form-body': "Your clinic hasn't added any questions to this form. Check back after they update it.",
        'attachments.failed': 'One or more file uploads failed. Retry them before submitting.',
        'attachments.pending': "Waiting for file uploads to finish. Submit will be enabled when they're done.",
        'findings.stale-heading': 'Verify your existing answers',
        'findings.clinical-heading': 'Review the highlighted items',
        'fields.select-placeholder': '— select —',
        'fields.yes': 'Yes',
        'fields.no': 'No',
        'fields.file-attached': 'File attached',
        'fields.help-prefix': 'Why we ask: ',
        'fields.country-label': 'Country',
        'fields.phone-hint': "Enter a phone number; we'll format it after you leave the field.",
        'fields.attachment.uploading': 'Uploading…',
        'fields.attachment.uploaded': 'Uploaded',
        'fields.attachment.failed': 'Upload failed',
        'fields.attachment.ready': 'Ready',
        'fields.attachment.ocr-low-confidence': 'We had trouble reading your card — please verify the auto-filled fields below.',
        'lang.picker-label': 'Language',
        // Patient step-wizard chrome (Clinical Slate, Pass 3).
        'wizard.secure': 'Private and secure',
        'wizard.welcome.body': 'This helps your care team get ready for your visit. It takes a few minutes. You can stop and finish later — your answers are saved.',
        'wizard.start': 'Start check-in',
        'wizard.resume': 'Pick up where you left off',
        'wizard.step-of': (a) => `Step ${String(a['current'])} of ${String(a['total'])}`,
        'wizard.back': 'Go back',
        'wizard.continue': 'Save and continue',
        'wizard.finish-later': 'Save and finish later',
        'wizard.send': 'Finish and send',
        'wizard.review.title': 'Review your answers',
        'wizard.review.help': 'Check your answers. You can change anything before you send.',
        'wizard.review.edit': 'Edit',
        'wizard.review.not-answered': 'Not answered yet',
        'wizard.confirm.next-heading': 'What happens next',
        'wizard.confirm.next-when-h': "When you'll hear back",
        'wizard.confirm.next-when-b': "Your care team reviews your answers before your visit. You don't need to do anything else.",
        'wizard.confirm.next-who-h': 'Who can see this',
        'wizard.confirm.next-who-b': 'Only the clinicians involved in your care.',
        'wizard.confirm.next-fix-h': 'Made a mistake?',
        'wizard.confirm.next-fix-b': 'Contact your clinic and they can update your answers.',
        // Validation findings — keyed by FindingCode, see validation/types.ts.
        'finding.required': 'This field is required.',
        'finding.type': 'Please enter a valid value.',
        'finding.regex': "That value doesn't match the expected format.",
        'finding.min': (a) => `Must be at least ${String(a['n'])}.`,
        'finding.max': (a) => `Must be at most ${String(a['n'])}.`,
        'finding.incomplete': (a) => `${String(a['n'])} required item(s) are not answered.`,
        'finding.dob-future': 'Date of birth cannot be in the future.',
        'finding.dob-implausible': (a) => `Age of ${String(a['age'])} years looks unusual — please check the date.`,
        'finding.phone-invalid': (a) => `Not a valid ${String(a['country'])} phone number.`,
        'finding.phone-unparseable': "That doesn't look like a phone number.",
    },
    es: {
        'submit.idle': 'Enviar respuesta',
        'submit.pending': 'Enviando…',
        'submit.success.heading': 'Envío exitoso',
        'submit.success.body': 'Gracias. Sus respuestas se han registrado de forma segura y se han enviado a su proveedor de atención médica. Ya puede cerrar esta ventana.',
        'submit.success.summary': 'Resumen de respuestas enviadas',
        'submit.success.no-answers': 'No se respondieron preguntas.',
        'submit.response-recorded': 'Respuesta registrada',
        'autosave.unsaved': 'Cambios sin guardar',
        'autosave.not-saved': 'Aún no guardado',
        'autosave.just-now': 'Guardado hace un momento',
        'autosave.seconds': (a) => `Guardado hace ${String(a['n'])} s`,
        'autosave.minutes': (a) => `Guardado hace ${String(a['n'])} min`,
        'autosave.hours': (a) => `Guardado hace ${String(a['n'])} h`,
        'header.required-count': (a) => `${String(a['done'])} / ${String(a['total'])} obligatorios`,
        'header.no-required': 'No hay preguntas obligatorias',
        'loading.form': 'Cargando su formulario',
        'empty.form-body': 'Su clínica aún no ha agregado preguntas a este formulario. Vuelva a consultarlo después.',
        'attachments.failed': 'No se pudieron cargar uno o más archivos. Vuelva a intentarlo antes de enviar.',
        'attachments.pending': 'Esperando que terminen las cargas de archivos. El botón Enviar se habilitará cuando finalicen.',
        'findings.stale-heading': 'Verifique sus respuestas anteriores',
        'findings.clinical-heading': 'Revise los elementos resaltados',
        'fields.select-placeholder': '— seleccionar —',
        'fields.yes': 'Sí',
        'fields.no': 'No',
        'fields.file-attached': 'Archivo adjunto',
        'fields.help-prefix': 'Por qué preguntamos: ',
        'fields.country-label': 'País',
        'fields.phone-hint': 'Introduzca un número de teléfono; lo formatearemos cuando salga del campo.',
        'fields.attachment.uploading': 'Cargando…',
        'fields.attachment.uploaded': 'Cargado',
        'fields.attachment.failed': 'Error de carga',
        'fields.attachment.ready': 'Listo',
        'fields.attachment.ocr-low-confidence': 'Tuvimos problemas para leer su tarjeta — verifique los campos completados automáticamente.',
        'lang.picker-label': 'Idioma',
        'wizard.secure': 'Privado y seguro',
        'wizard.welcome.body': 'Esto ayuda a su equipo de salud a prepararse para su visita. Toma unos minutos. Puede parar y terminar después — sus respuestas se guardan.',
        'wizard.start': 'Comenzar registro',
        'wizard.resume': 'Continuar donde quedó',
        'wizard.step-of': (a) => `Paso ${String(a['current'])} de ${String(a['total'])}`,
        'wizard.back': 'Regresar',
        'wizard.continue': 'Guardar y seguir',
        'wizard.finish-later': 'Guardar y terminar después',
        'wizard.send': 'Terminar y enviar',
        'wizard.review.title': 'Revise sus respuestas',
        'wizard.review.help': 'Revise sus respuestas. Puede cambiar lo que quiera antes de enviar.',
        'wizard.review.edit': 'Editar',
        'wizard.review.not-answered': 'Sin responder',
        'wizard.confirm.next-heading': 'Qué sigue',
        'wizard.confirm.next-when-h': 'Cuándo sabrá algo',
        'wizard.confirm.next-when-b': 'Su equipo de salud revisa sus respuestas antes de su visita. No tiene que hacer nada más.',
        'wizard.confirm.next-who-h': 'Quién puede verlo',
        'wizard.confirm.next-who-b': 'Solo los profesionales que participan en su atención.',
        'wizard.confirm.next-fix-h': '¿Cometió un error?',
        'wizard.confirm.next-fix-b': 'Comuníquese con su clínica y podrán actualizar sus respuestas.',
        'finding.required': 'Este campo es obligatorio.',
        'finding.type': 'Introduzca un valor válido.',
        'finding.regex': 'Ese valor no coincide con el formato esperado.',
        'finding.min': (a) => `Debe ser al menos ${String(a['n'])}.`,
        'finding.max': (a) => `Debe ser como máximo ${String(a['n'])}.`,
        'finding.incomplete': (a) => `${String(a['n'])} elemento(s) obligatorio(s) sin responder.`,
        'finding.dob-future': 'La fecha de nacimiento no puede estar en el futuro.',
        'finding.dob-implausible': (a) => `Una edad de ${String(a['age'])} años parece inusual — por favor verifique la fecha.`,
        'finding.phone-invalid': (a) => `No es un número de teléfono válido de ${String(a['country'])}.`,
        'finding.phone-unparseable': 'Eso no parece un número de teléfono.',
    },
    vi: {
        'submit.idle': 'Gửi câu trả lời',
        'submit.pending': 'Đang gửi…',
        'submit.success.heading': 'Gửi thành công',
        'submit.success.body': 'Cảm ơn bạn. Câu trả lời của bạn đã được ghi lại an toàn và gửi đến nhà cung cấp dịch vụ chăm sóc sức khỏe. Bạn có thể đóng cửa sổ này.',
        'submit.success.summary': 'Tóm tắt câu trả lời đã gửi',
        'submit.success.no-answers': 'Không có câu hỏi nào được trả lời.',
        'submit.response-recorded': 'Đã ghi nhận câu trả lời',
        'autosave.unsaved': 'Có thay đổi chưa lưu',
        'autosave.not-saved': 'Chưa được lưu',
        'autosave.just-now': 'Vừa lưu xong',
        'autosave.seconds': (a) => `Đã lưu cách đây ${String(a['n'])} giây`,
        'autosave.minutes': (a) => `Đã lưu cách đây ${String(a['n'])} phút`,
        'autosave.hours': (a) => `Đã lưu cách đây ${String(a['n'])} giờ`,
        'header.required-count': (a) => `${String(a['done'])} / ${String(a['total'])} bắt buộc`,
        'header.no-required': 'Không có câu hỏi bắt buộc',
        'loading.form': 'Đang tải biểu mẫu',
        'empty.form-body': 'Phòng khám của bạn chưa thêm câu hỏi vào biểu mẫu này. Vui lòng quay lại sau.',
        'attachments.failed': 'Một hoặc nhiều tệp tải lên đã thất bại. Vui lòng thử lại trước khi gửi.',
        'attachments.pending': 'Đang chờ tải tệp lên. Nút Gửi sẽ được kích hoạt khi hoàn tất.',
        'findings.stale-heading': 'Xác minh câu trả lời trước đây của bạn',
        'findings.clinical-heading': 'Xem lại các mục được đánh dấu',
        'fields.select-placeholder': '— chọn —',
        'fields.yes': 'Có',
        'fields.no': 'Không',
        'fields.file-attached': 'Tệp đính kèm',
        'fields.help-prefix': 'Lý do chúng tôi hỏi: ',
        'fields.country-label': 'Quốc gia',
        'fields.phone-hint': 'Nhập số điện thoại; chúng tôi sẽ định dạng sau khi bạn rời khỏi ô.',
        'fields.attachment.uploading': 'Đang tải lên…',
        'fields.attachment.uploaded': 'Đã tải lên',
        'fields.attachment.failed': 'Tải lên thất bại',
        'fields.attachment.ready': 'Sẵn sàng',
        'fields.attachment.ocr-low-confidence': 'Chúng tôi gặp khó khăn khi đọc thẻ của bạn — vui lòng xác minh các trường đã tự động điền.',
        'lang.picker-label': 'Ngôn ngữ',
        'wizard.secure': 'Riêng tư và an toàn',
        'wizard.welcome.body': 'Việc này giúp nhóm chăm sóc chuẩn bị cho buổi khám của bạn. Mất vài phút. Bạn có thể dừng và làm tiếp sau — câu trả lời sẽ được lưu.',
        'wizard.start': 'Bắt đầu nhận bệnh',
        'wizard.resume': 'Tiếp tục chỗ đã dừng',
        'wizard.step-of': (a) => `Bước ${String(a['current'])} trên ${String(a['total'])}`,
        'wizard.back': 'Quay lại',
        'wizard.continue': 'Lưu và tiếp tục',
        'wizard.finish-later': 'Lưu và làm tiếp sau',
        'wizard.send': 'Hoàn tất và gửi',
        'wizard.review.title': 'Xem lại câu trả lời',
        'wizard.review.help': 'Kiểm tra câu trả lời. Bạn có thể thay đổi bất cứ điều gì trước khi gửi.',
        'wizard.review.edit': 'Sửa',
        'wizard.review.not-answered': 'Chưa trả lời',
        'wizard.confirm.next-heading': 'Tiếp theo là gì',
        'wizard.confirm.next-when-h': 'Khi nào có phản hồi',
        'wizard.confirm.next-when-b': 'Nhóm chăm sóc sẽ xem câu trả lời trước buổi khám của bạn. Bạn không cần làm gì thêm.',
        'wizard.confirm.next-who-h': 'Ai có thể xem',
        'wizard.confirm.next-who-b': 'Chỉ những bác sĩ tham gia chăm sóc bạn.',
        'wizard.confirm.next-fix-h': 'Nhập sai?',
        'wizard.confirm.next-fix-b': 'Hãy liên hệ phòng khám và họ có thể cập nhật câu trả lời của bạn.',
        'finding.required': 'Trường này là bắt buộc.',
        'finding.type': 'Vui lòng nhập giá trị hợp lệ.',
        'finding.regex': 'Giá trị này không khớp với định dạng yêu cầu.',
        'finding.min': (a) => `Phải lớn hơn hoặc bằng ${String(a['n'])}.`,
        'finding.max': (a) => `Phải nhỏ hơn hoặc bằng ${String(a['n'])}.`,
        'finding.incomplete': (a) => `${String(a['n'])} mục bắt buộc chưa được trả lời.`,
        'finding.dob-future': 'Ngày sinh không thể ở tương lai.',
        'finding.dob-implausible': (a) => `Tuổi ${String(a['age'])} có vẻ bất thường — vui lòng kiểm tra lại ngày.`,
        'finding.phone-invalid': (a) => `Không phải là số điện thoại ${String(a['country'])} hợp lệ.`,
        'finding.phone-unparseable': 'Đây không có vẻ là một số điện thoại.',
    },
};
/**
 * Resolve a chrome string for the given language. Falls back to
 * English if the language has no entry for the key; warns in dev so
 * the gap is visible without breaking the form.
 */
export function translate(lang, key, args) {
    const langMap = DICTIONARY[lang];
    const value = langMap[key] ?? DICTIONARY.en[key];
    if (value === undefined) {
        // Unknown key — surface in dev so it doesn't ship unnoticed.
        if (typeof process !== 'undefined' && process.env?.['NODE_ENV'] !== 'production') {
            // Browser devtools only (the SPA has no server runtime), dev builds only.
            // eslint-disable-next-line no-console
            console.warn(`[i18n] missing translation key: ${key}`);
        }
        return key;
    }
    if (typeof value === 'function')
        return value(args ?? {});
    return value;
}
//# sourceMappingURL=dictionary.js.map